import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Button, Select } from '@/components/ui';
import { apiFetch } from '@/data/api';
import { parseCsv } from '@/lib/csv';
import { useMe } from '@/auth/useMe';
import type { Lifecycle } from '@/data/types';
import { LIFECYCLE_OPTIONS } from './LifecycleBadge';
import { FormError, Modal } from './Modal';

type Field = 'name' | 'website' | 'domain' | 'phone' | 'address' | 'city' | 'state' | 'industry' | 'services' | 'serviceAreas'
  | 'lifecycle' | 'notes' | 'contactFirstName' | 'contactLastName' | 'contactEmail' | 'contactPhone' | 'contactTitle' | 'tritoniqClientId';

// Header auto-detection, most specific first.
const FIELDS: { key: Field; label: string; re: RegExp; agencyOnly?: boolean }[] = [
  { key: 'tritoniqClientId', label: 'tritonIQ client id', re: /triton.?iq|client.?id|org.?id/i, agencyOnly: true },
  { key: 'contactFirstName', label: 'Contact first name', re: /(contact|first).*(first|name)|^first/i },
  { key: 'contactLastName', label: 'Contact last name', re: /(contact|last).*(last|name)|^last|surname/i },
  { key: 'contactEmail', label: 'Contact email', re: /e-?mail/i },
  { key: 'contactTitle', label: 'Contact title', re: /title|position|role/i },
  { key: 'contactPhone', label: 'Contact phone', re: /(contact|mobile|cell).*phone|mobile|cell/i },
  { key: 'name', label: 'Company name', re: /^(company|business|client|account|organi[sz]ation)?\s*name$|^company$|^business$|^client$|^account$/i },
  { key: 'website', label: 'Website', re: /web|url|site/i },
  { key: 'domain', label: 'Email domain', re: /domain/i },
  { key: 'phone', label: 'Company phone', re: /phone|tel/i },
  { key: 'address', label: 'Address', re: /address|street/i },
  { key: 'city', label: 'City', re: /city|town/i },
  { key: 'state', label: 'State', re: /^state|province|region/i },
  { key: 'industry', label: 'Industry', re: /industry|type|vertical|category/i },
  { key: 'serviceAreas', label: 'Service areas', re: /area|market|territor/i },
  { key: 'services', label: 'Services', re: /service|product|offer/i },
  { key: 'lifecycle', label: 'Lifecycle', re: /lifecycle|status|stage/i },
  { key: 'notes', label: 'Notes', re: /note|comment/i },
];

interface Result { dryRun: boolean; summary: Record<'create' | 'update' | 'unchanged' | 'error', number>; results: { row: number; name: string | null; outcome: string; message?: string }[] }

/* CSV import for companies (with an optional contact per row). In Google Sheets:
   File → Download → Comma-separated values. Always previews first; the preview
   runs the real matching rules without saving. */
export function ImportCompaniesModal({ onClose }: { onClose: () => void }) {
  const qc = useQueryClient();
  const { me } = useMe();
  const [csv, setCsv] = useState<{ headers: string[]; rows: string[][]; file: string } | null>(null);
  const [map, setMap] = useState<Record<number, Field | ''>>({});
  const [lifecycle, setLifecycle] = useState<Lifecycle>('past_client');
  const [preview, setPreview] = useState<Result | null>(null);
  const [stale, setStale] = useState(false);
  const fields = FIELDS.filter((f) => !f.agencyOnly || me?.workspace.isAgency);

  const load = async (file: File) => {
    const parsed = parseCsv(await file.text());
    const used = new Set<Field>();
    const auto: Record<number, Field | ''> = {};
    parsed.headers.forEach((h, i) => {
      const f = fields.find((x) => !used.has(x.key) && x.re.test(h));
      auto[i] = f?.key ?? '';
      if (f) used.add(f.key);
    });
    setCsv({ ...parsed, file: file.name });
    setMap(auto);
    setPreview(null);
    setStale(true);
  };

  const rows = useMemo(() => (csv?.rows ?? []).map((cells) => {
    const o: Record<string, string> = {};
    for (const [i, key] of Object.entries(map)) if (key) o[key] = cells[Number(i)] ?? '';
    return o;
  }), [csv, map]);

  const run = useMutation({
    mutationFn: (dryRun: boolean) => apiFetch<Result>('/api/import/companies', { method: 'POST', body: JSON.stringify({ rows, defaultLifecycle: lifecycle, dryRun }) }),
    onSuccess: (r) => {
      setPreview(r);
      if (!r.dryRun) for (const k of ['companies', 'contacts', 'audit']) void qc.invalidateQueries({ queryKey: [k] });
    },
  });
  const hasName = Object.values(map).includes('name');
  const done = preview && !preview.dryRun;
  // The preview re-runs by itself whenever the file, mapping or lifecycle
  // changes, so the button always reflects exactly what Import will do.
  useEffect(() => {
    if (!stale || !csv || !hasName || run.isPending) return;
    setStale(false);
    run.mutate(true);
  }, [stale, csv, hasName, run]);
  const importCount = preview?.dryRun ? preview.summary.create + preview.summary.update : 0;
  const status = !csv ? 'Choose a CSV file to begin.'
    : !hasName ? 'Choose which column is the company name.'
    : run.isPending ? (preview?.dryRun === false ? 'Importing…' : 'Checking your file…')
    : preview?.dryRun && importCount === 0 ? 'Nothing new to import — everything already matches.'
    : null;

  return (
    <Modal title="Import companies" wide onClose={onClose} footer={done
      ? <Button onClick={onClose}>Done</Button>
      : <>
        {status && <span className="crm-cell-sub" style={{ marginRight: 'auto' }}>{status}</span>}
        <Button variant="ghost" onClick={onClose}>Cancel</Button>
        <Button onClick={() => run.mutate(false)} disabled={!preview?.dryRun || !importCount || run.isPending}>
          {preview?.dryRun && importCount ? `Import ${importCount} ${importCount === 1 ? 'company' : 'companies'}` : 'Import'}
        </Button>
      </>}>
      <div className="crm-form">
        <p className="crm-cell-muted">
          Upload a CSV — from Google Sheets use <strong>File → Download → Comma-separated values</strong>. Existing companies are matched
          {me?.workspace.isAgency ? ' by tritonIQ client id, ' : ' '}by website domain, then by exact name, and only gain fields that are empty — nothing is overwritten.
        </p>
        <input type="file" accept=".csv,text/csv" onChange={(e) => { const f = e.target.files?.[0]; if (f) void load(f); }} />
        {csv && (
          <>
            <div className="crm-grid2">
              <Select label="Lifecycle when the sheet has none" options={LIFECYCLE_OPTIONS} value={lifecycle}
                onChange={(e) => { setLifecycle(e.target.value as Lifecycle); setPreview(null); setStale(true); }} />
              <p className="crm-cell-sub" style={{ alignSelf: 'end' }}>{csv.rows.length} rows in {csv.file}</p>
            </div>
            <table className="dash-table crm-map">
              <thead><tr><th>Column in your file</th><th>Example</th><th>Import as</th></tr></thead>
              <tbody>
                {csv.headers.map((h, i) => (
                  <tr key={i}>
                    <td><strong>{h || `Column ${i + 1}`}</strong></td>
                    <td className="crm-cell-muted">{csv.rows[0]?.[i]?.slice(0, 40)}</td>
                    <td>
                      <select className="crm-inline-select" value={map[i] ?? ''} onChange={(e) => { setMap({ ...map, [i]: e.target.value as Field | '' }); setPreview(null); setStale(true); }}>
                        <option value="">Skip</option>
                        {fields.map((f) => <option key={f.key} value={f.key}>{f.label}</option>)}
                      </select>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {!hasName && <p className="crm-error">Choose which column is the company name.</p>}
          </>
        )}
        {preview && <ImportSummary result={preview} />}
        <FormError error={run.error} />
      </div>
    </Modal>
  );
}

function ImportSummary({ result }: { result: Result }) {
  const s = result.summary;
  const notable = result.results.filter((r) => r.outcome === 'error' || r.message);
  return (
    <div className="crm-callout">
      <strong>{result.dryRun ? 'Preview — nothing saved yet' : 'Imported'}</strong>
      <p>{s.create} new · {s.update} updated · {s.unchanged} unchanged · {s.error} with errors</p>
      {notable.length > 0 && (
        <ul className="crm-import-notes">
          {notable.slice(0, 50).map((r) => <li key={r.row}>Row {r.row}{r.name ? ` (${r.name})` : ''}: {r.message ?? r.outcome}</li>)}
          {notable.length > 50 && <li>…and {notable.length - 50} more</li>}
        </ul>
      )}
    </div>
  );
}
